import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconChecklist,
  IconClockHour4,
  IconInfoCircle,
  IconPackageOff,
  IconReceipt2,
  IconRotate,
  IconSearch,
  IconSend,
  IconShieldCheck,
  IconTruckReturn,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

const receiptIssuesSeed = [
  {
    id: 'GRN-2026-0021',
    returnCandidateId: 'RET-CAND-001',
    requisitionId: 'PR-2026-0105',
    requisitionTitle: 'Yeni personel için çalışma ekipmanı talebi',
    vendorName: 'OfisDonanım Ltd.',
    department: 'Bilgi İşlem',
    receivedAt: '2026-03-20 10:30',
    receivedBy: 'Depo Sorumlusu',
    receiptStatus: 'CONFIRMED',
    issueCount: 2,
    issues: [
      {
        goodsReceiptLineId: 'GRL-001',
        lineNo: 1,
        description: 'Dell Latitude 15 Laptop',
        orderedQty: 1,
        receivedQty: 1,
        acceptedQty: 0,
        rejectedQty: 1,
        uom: 'Adet',
        conditionStatus: 'DAMAGED',
        defaultReasonCode: 'DAMAGED',
        note: 'Laptop gövdesinde darbe izi mevcut.',
      },
      {
        goodsReceiptLineId: 'GRL-003',
        lineNo: 3,
        description: 'Kablosuz Mouse',
        orderedQty: 1,
        receivedQty: 0,
        acceptedQty: 0,
        rejectedQty: 0,
        uom: 'Adet',
        conditionStatus: 'PARTIAL',
        defaultReasonCode: 'MISSING',
        note: 'Sevkiyatta kalem eksik çıktı.',
      },
    ],
  },
  {
    id: 'GRN-2026-0023',
    returnCandidateId: 'RET-CAND-002',
    requisitionId: 'PR-2026-0111',
    requisitionTitle: 'Şantiye personeli koruyucu ekipman alımı',
    vendorName: 'Anadolu Endüstri',
    department: 'Şantiye Operasyon',
    receivedAt: '2026-03-21 09:15',
    receivedBy: 'Depo Sorumlusu',
    receiptStatus: 'CONFIRMED',
    issueCount: 1,
    issues: [
      {
        goodsReceiptLineId: 'GRL-011',
        lineNo: 2,
        description: 'Reflektif Yelek',
        orderedQty: 40,
        receivedQty: 40,
        acceptedQty: 36,
        rejectedQty: 4,
        uom: 'Adet',
        conditionStatus: 'WRONG_ITEM',
        defaultReasonCode: 'WRONG_ITEM',
        note: '4 adet yanlış beden gönderildi.',
      },
    ],
  },
];

const existingReturnsSeed = [
  {
    id: 'RET-2026-0009',
    goodsReceiptId: 'GRN-2026-0018',
    vendorName: 'Tekno Tedarik A.Ş.',
    returnDate: '2026-03-18',
    status: 'SENT',
    reasonSummary: 'Hasarlı ürün',
    totalQty: 2,
  },
  {
    id: 'RET-2026-0010',
    goodsReceiptId: 'GRN-2026-0019',
    vendorName: 'Kurumsal Market',
    returnDate: '2026-03-19',
    status: 'DRAFT',
    reasonSummary: 'Yanlış ürün',
    totalQty: 1,
  },
];

const reasonOptions = [
  { value: 'DAMAGED', label: 'Hasarlı Ürün' },
  { value: 'WRONG_ITEM', label: 'Yanlış Ürün' },
  { value: 'EXCESS', label: 'Fazla Ürün' },
  { value: 'QUALITY', label: 'Kalite Problemi' },
  { value: 'MISSING', label: 'Eksik Teslim / Tamamlama Bekleniyor' },
  { value: 'OTHER', label: 'Diğer' },
];

const statusConfig = {
  CONFIRMED: { label: 'Kesinleşti', color: 'teal' },
  DRAFT: { label: 'Taslak', color: 'gray' },
  SENT: { label: 'Gönderildi', color: 'blue' },
  CANCELLED: { label: 'İptal', color: 'red' },
  DAMAGED: { label: 'Hasarlı', color: 'red' },
  WRONG_ITEM: { label: 'Yanlış Ürün', color: 'violet' },
  PARTIAL: { label: 'Eksik Teslim', color: 'orange' },
  QUALITY: { label: 'Kalite Problemi', color: 'yellow' },
  MISSING: { label: 'Eksik Kalem', color: 'orange' },
  EXCESS: { label: 'Fazla Ürün', color: 'grape' },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { label: status, color: 'gray' };
  return (
    <Badge variant="light" color={cfg.color} radius="sm">
      {cfg.label}
    </Badge>
  );
}

function SummaryCard({ title, value, subtitle, color, icon: Icon }) {
  return (
    <Card withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" fw={500}>{title}</Text>
          <Text fw={800} size="1.55rem" lh={1.1}>{value}</Text>
          <Text size="xs" c="dimmed">{subtitle}</Text>
        </Stack>
        <ThemeIcon color={color} variant="light" size={46} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <Paper withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={4}>{title}</Title>
          {description ? <Text size="sm" c="dimmed" mt={4}>{description}</Text> : null}
        </div>
        {action}
      </Group>
      {children}
    </Paper>
  );
}

function InfoRow({ label, value }) {
  return (
    <>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">{label}</Text>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Text size="sm" fw={600} ta="right">{value || '-'}</Text>
        ) : value}
      </Group>
      <Divider />
    </>
  );
}

export default function ProcurementReturnManagementPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState(receiptIssuesSeed[0]?.returnCandidateId || '');
  const [returnHeader, setReturnHeader] = useState({
    returnNo: 'RET-2026-0011',
    returnDate: '2026-03-20',
    returnedBy: 'Depo Sorumlusu',
    status: 'DRAFT',
    carrierInfo: 'Şirket aracı ile gönderim planlandı',
    note: 'Hasarlı ve eksik teslim kalemler için iade / düzeltme kaydı açıldı.',
  });

  const [returnLines, setReturnLines] = useState([
    {
      goodsReceiptLineId: 'GRL-001',
      lineNo: 1,
      description: 'Dell Latitude 15 Laptop',
      quantity: 1,
      uom: 'Adet',
      reasonCode: 'DAMAGED',
      note: 'Kasa ve köşe kısmında ezik mevcut.',
    },
    {
      goodsReceiptLineId: 'GRL-003',
      lineNo: 3,
      description: 'Kablosuz Mouse',
      quantity: 1,
      uom: 'Adet',
      reasonCode: 'MISSING',
      note: 'Eksik sevkiyat nedeniyle tamamlama bekleniyor.',
    },
  ]);

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receiptIssuesSeed;
    return receiptIssuesSeed.filter((item) => JSON.stringify(item).toLowerCase().includes(q));
  }, [search]);

  const selectedCandidate = useMemo(() => {
    return receiptIssuesSeed.find((item) => item.returnCandidateId === selectedCandidateId) || receiptIssuesSeed[0] || null;
  }, [selectedCandidateId]);

  const stats = useMemo(() => {
    return {
      candidates: receiptIssuesSeed.length,
      issueLines: receiptIssuesSeed.reduce((acc, item) => acc + item.issueCount, 0),
      sentReturns: existingReturnsSeed.filter((item) => item.status === 'SENT').length,
      draftReturns: existingReturnsSeed.filter((item) => item.status === 'DRAFT').length,
    };
  }, []);

  const totals = useMemo(() => {
    const lineCount = returnLines.length;
    const totalQty = returnLines.reduce((acc, line) => acc + Number(line.quantity || 0), 0);
    const damagedCount = returnLines.filter((line) => line.reasonCode === 'DAMAGED').length;
    return { lineCount, totalQty, damagedCount };
  }, [returnLines]);

  const updateHeader = (field, value) => {
    setReturnHeader((prev) => ({ ...prev, [field]: value }));
  };

  const updateLine = (lineNo, field, value) => {
    setReturnLines((prev) => prev.map((line) => (line.lineNo === lineNo ? { ...line, [field]: value } : line)));
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          withBorder
          radius="xl"
          p="xl"
          shadow="sm"
          style={{
            background: 'linear-gradient(135deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
          }}
        >
          <Group justify="space-between" align="flex-start" gap="xl">
            <Stack gap="sm" maw={860}>
              <Group gap="sm">
                <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} px={0} onClick={() => navigate("/purchase-dashboard")}>
                  Geri Dön
                </Button>
                <Badge variant="light" color="orange" leftSection={<IconTruckReturn size={14} />}>
                  Statik demo iade ekranı
                </Badge>
              </Group>
              <div>
                <Title order={1}>İade Yönetimi</Title>
                <Text size="sm" c="dimmed" mt="sm">
                  Mal kabul sırasında sorunlu bulunan kalemler için iade veya düzeltme kayıtlarının oluşturulduğu ekran.
                  Hasarlı, yanlış gelen veya eksik teslim edilen ürünlerin tedarikçiye geri dönüş süreci burada yönetilir.
                </Text>
              </div>
            </Stack>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="md" mt="xl">
            <SummaryCard title="İade Adayı" value={stats.candidates} subtitle="Sorunlu mal kabul kaydı" color="orange" icon={IconAlertTriangle} />
            <SummaryCard title="Sorunlu Satır" value={stats.issueLines} subtitle="İşlem bekleyen kalem" color="red" icon={IconPackageOff} />
            <SummaryCard title="Gönderilen İade" value={stats.sentReturns} subtitle="Tedarikçiye iletilmiş kayıt" color="blue" icon={IconSend} />
            <SummaryCard title="Taslak İade" value={stats.draftReturns} subtitle="Henüz tamamlanmamış kayıt" color="gray" icon={IconClockHour4} />
          </SimpleGrid>
        </Paper>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, xl: 3.5 }}>
            <Stack gap="md">
              <SectionCard title="İade Aday Havuzu" description="Sorun tespit edilen mal kabullerden seçim yapın.">
                <Stack gap="sm">
                  <TextInput
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    placeholder="Mal kabul, talep, tedarikçi ara..."
                    leftSection={<IconSearch size={16} />}
                  />

                  <Stack gap="xs">
                    {filteredCandidates.map((item) => (
                      <Paper
                        key={item.returnCandidateId}
                        withBorder
                        radius="md"
                        p="sm"
                        onClick={() => setSelectedCandidateId(item.returnCandidateId)}
                        style={{
                          cursor: 'pointer',
                          borderColor: selectedCandidateId === item.returnCandidateId ? 'var(--mantine-color-orange-4)' : undefined,
                          backgroundColor: selectedCandidateId === item.returnCandidateId ? 'var(--mantine-color-orange-0)' : undefined,
                        }}
                      >
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <div>
                            <Text fw={700} size="sm">{item.id}</Text>
                            <Text size="sm" mt={2}>{item.requisitionTitle}</Text>
                            <Text size="xs" c="dimmed" mt={4}>{item.vendorName} • {item.department}</Text>
                          </div>
                          <Stack gap={6} align="flex-end">
                            <Badge variant="light" color="orange">{item.issueCount} sorun</Badge>
                            <StatusBadge status={item.receiptStatus} />
                          </Stack>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </SectionCard>

              <SectionCard title="Mevcut İadeler" description="Daha önce açılmış örnek kayıtlar.">
                <Stack gap="xs">
                  {existingReturnsSeed.map((item) => (
                    <Paper key={item.id} withBorder radius="md" p="sm">
                      <Group justify="space-between" align="flex-start">
                        <div>
                          <Text fw={700} size="sm">{item.id}</Text>
                          <Text size="xs" c="dimmed" mt={4}>{item.vendorName} • {item.goodsReceiptId}</Text>
                        </div>
                        <Stack gap={6} align="flex-end">
                          <StatusBadge status={item.status} />
                          <Badge variant="light" color="gray">{item.totalQty} adet</Badge>
                        </Stack>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </SectionCard>

              <SectionCard title="Süreç Akışı" description="Bu ekranın satın alma zincirindeki rolü.">
                <Stack gap="sm">
                  <Paper withBorder radius="md" p="sm">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="cyan" variant="light" mt={2}><IconChecklist size={16} /></ThemeIcon>
                      <div>
                        <Text size="sm" fw={700}>1. Mal Kabul Yapıldı</Text>
                        <Text size="xs" c="dimmed" mt={2}>Teslim alınan kalemler kontrol edildi.</Text>
                      </div>
                    </Group>
                  </Paper>
                  <Paper withBorder radius="md" p="sm">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="orange" variant="light" mt={2}><IconRotate size={16} /></ThemeIcon>
                      <div>
                        <Text size="sm" fw={700}>2. İade Açılıyor</Text>
                        <Text size="xs" c="dimmed" mt={2}>Sorunlu satırlar için bu ekranda kayıt oluşturuluyor.</Text>
                      </div>
                    </Group>
                  </Paper>
                  <Paper withBorder radius="md" p="sm">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="blue" variant="light" mt={2}><IconReceipt2 size={16} /></ThemeIcon>
                      <div>
                        <Text size="sm" fw={700}>3. Fatura / Düzeltme</Text>
                        <Text size="xs" c="dimmed" mt={2}>Kabul edilen kalemler fatura sürecine aktarılır.</Text>
                      </div>
                    </Group>
                  </Paper>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 8.5 }}>
            <Stack gap="lg">
              {selectedCandidate && (
                <>
                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="Kaynak Mal Kabul Özeti" description="İade kaydının bağlandığı teslim alma bilgileri.">
                      <Stack gap="sm">
                        <InfoRow label="Mal Kabul No" value={selectedCandidate.id} />
                        <InfoRow label="Talep No" value={selectedCandidate.requisitionId} />
                        <InfoRow label="Talep Başlığı" value={selectedCandidate.requisitionTitle} />
                        <InfoRow label="Tedarikçi" value={selectedCandidate.vendorName} />
                        <InfoRow label="Departman" value={selectedCandidate.department} />
                        <InfoRow label="Teslim Alan" value={selectedCandidate.receivedBy} />
                        <InfoRow label="Teslim Zamanı" value={selectedCandidate.receivedAt} />
                        <InfoRow label="Sorunlu Satır" value={selectedCandidate.issueCount} />
                        <InfoRow label="Mal Kabul Durumu" value={<StatusBadge status={selectedCandidate.receiptStatus} />} />
                      </Stack>
                    </SectionCard>

                    <SectionCard title="İade Başlık Bilgileri" description="Oluşturulacak iade belgesinin üst alanları.">
                      <Grid gutter="md">
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="İade No"
                            value={returnHeader.returnNo}
                            onChange={(event) => updateHeader('returnNo', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="İade Tarihi"
                            type="date"
                            value={returnHeader.returnDate}
                            onChange={(event) => updateHeader('returnDate', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="İade Eden"
                            value={returnHeader.returnedBy}
                            onChange={(event) => updateHeader('returnedBy', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Select
                            label="Durum"
                            data={[
                              { value: 'DRAFT', label: 'Taslak' },
                              { value: 'SENT', label: 'Gönderildi' },
                              { value: 'CANCELLED', label: 'İptal' },
                            ]}
                            value={returnHeader.status}
                            onChange={(value) => updateHeader('status', value || 'DRAFT')}
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <TextInput
                            label="Taşıma / Gönderim Bilgisi"
                            value={returnHeader.carrierInfo}
                            onChange={(event) => updateHeader('carrierInfo', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Textarea
                            label="Genel Not"
                            minRows={4}
                            value={returnHeader.note}
                            onChange={(event) => updateHeader('note', event.currentTarget.value)}
                          />
                        </Grid.Col>
                      </Grid>
                    </SectionCard>
                  </SimpleGrid>

                  <SectionCard title="İade Satırları" description="Mal kabulde problem tespit edilen kalemleri iade veya düzeltme için işleyin.">
                    <Stack gap="md">
                      {returnLines.map((line) => (
                        <Paper key={`${line.goodsReceiptLineId}-${line.lineNo}`} withBorder radius="md" p="md">
                          <Stack gap="md">
                            <Group justify="space-between" align="center">
                              <Group gap="xs">
                                <ThemeIcon color="orange" variant="light" radius="md">
                                  <IconPackageOff size={16} />
                                </ThemeIcon>
                                <div>
                                  <Text fw={700}>İade Satırı {line.lineNo}</Text>
                                  <Text size="xs" c="dimmed">{line.description}</Text>
                                </div>
                              </Group>
                              <StatusBadge status={line.reasonCode} />
                            </Group>

                            <Grid gutter="md">
                              <Grid.Col span={{ base: 12, md: 3 }}>
                                <TextInput label="Mal Kabul Satırı" value={line.goodsReceiptLineId} readOnly />
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, md: 3 }}>
                                <NumberInput
                                  label="İade Miktarı"
                                  value={line.quantity}
                                  min={0}
                                  onChange={(value) => updateLine(line.lineNo, 'quantity', Number(value) || 0)}
                                />
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, md: 3 }}>
                                <TextInput label="Birim" value={line.uom} readOnly />
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, md: 3 }}>
                                <Select
                                  label="Sebep"
                                  data={reasonOptions}
                                  value={line.reasonCode}
                                  onChange={(value) => updateLine(line.lineNo, 'reasonCode', value || 'OTHER')}
                                />
                              </Grid.Col>
                              <Grid.Col span={12}>
                                <Textarea
                                  label="Satır Notu"
                                  minRows={2}
                                  value={line.note}
                                  onChange={(event) => updateLine(line.lineNo, 'note', event.currentTarget.value)}
                                />
                              </Grid.Col>
                            </Grid>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </SectionCard>

                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="İade Özeti" description="Oluşturulacak kaydın özet görünümü.">
                      <Stack gap="sm">
                        <InfoRow label="İade No" value={returnHeader.returnNo} />
                        <InfoRow label="İade Tarihi" value={returnHeader.returnDate} />
                        <InfoRow label="İade Eden" value={returnHeader.returnedBy} />
                        <InfoRow label="Toplam Satır" value={totals.lineCount} />
                        <InfoRow label="Toplam Miktar" value={totals.totalQty} />
                        <InfoRow label="Hasarlı Satır" value={<Badge variant="light" color="red">{totals.damagedCount}</Badge>} />
                        <InfoRow label="Durum" value={<StatusBadge status={returnHeader.status} />} />
                      </Stack>
                    </SectionCard>

                    <SectionCard title="Kaynak Sorun Kayıtları" description="Mal kabulden gelen ham problem alanları.">
                      <Stack gap="sm">
                        {selectedCandidate.issues.map((issue) => (
                          <Paper key={issue.goodsReceiptLineId} withBorder radius="md" p="sm">
                            <Group justify="space-between" align="flex-start">
                              <div>
                                <Text fw={700} size="sm">{issue.description}</Text>
                                <Text size="xs" c="dimmed" mt={4}>{issue.note}</Text>
                              </div>
                              <Stack gap={6} align="flex-end">
                                <StatusBadge status={issue.conditionStatus} />
                                <Badge variant="light" color="gray">Satır {issue.lineNo}</Badge>
                              </Stack>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </SectionCard>
                  </SimpleGrid>

                  <SectionCard
                    title="Aksiyonlar"
                    description="İade kaydı sonrası ilerleyecek adımlar için demo butonlar."
                    action={<Button variant="default" leftSection={<IconSearch size={16} />}>İade Formu Ön İzleme</Button>}
                  >
                    <SimpleGrid cols={{ base: 1, md: 4 }} spacing="sm">
                      <Button color="blue" leftSection={<IconCheck size={16} />}>İade Kaydını Kaydet</Button>
                      <Button variant="light" color="orange" leftSection={<IconSend size={16} />}>Tedarikçiye Gönderildi İşaretle</Button>
                      <Button variant="light" color="teal" leftSection={<IconShieldCheck size={16} />}>Düzeltme Bekleniyor</Button>
                      <Button variant="light" color="gray" leftSection={<IconReceipt2 size={16} />}>Fatura Aşamasına Geç</Button>
                    </SimpleGrid>
                  </SectionCard>
                </>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        <Paper withBorder radius="lg" p="md" shadow="sm" style={{ borderStyle: 'dashed' }}>
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon color="blue" variant="light" mt={2}><IconInfoCircle size={16} /></ThemeIcon>
            <Text size="sm" c="dimmed">
              Sonraki doğal ekran “Fatura Kayıt / Fatura Kontrol Ekranı” olur. Böylece kabul edilen kalemlerin finansal kapanış tarafını da tamamlayıp,
              satın alma sürecinin baştan sona demo zincirini kapatabiliriz.
            </Text>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}
